import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import loadjs from 'loadjs';

import eventBus from 'common/plugins/eventBus';

@Component({
  props: {
    mapConfig: {
      type: Object,
      required: true,
    },
    apiKey: {
      type: String,
      required: true,
    },
    markers: {
      type: Array,
      required: true,
    },
    markersHaveInfoWindow: {
      type: Boolean,
      default: false,
      description: 'Set markers click listener',
    },
  },
})
export default class BBGoogleMapLoaderComponent extends Vue {
  google = null;
  map = null;
  gmapsMarkers = null;

  async mounted() {
    [this.google] = await Promise.all([
      this.loadMapScript(),
      this.loadClusterScript(),
    ]);
    this.initializeMap();
    this.initializeMarkers();
    this.recreateClusters();
    this.recreateMarkers();
    if (this.markersHaveInfoWindow) {
      this.addMarkersClickListeners();
    }
  }

  beforeDestroy() {
    if (this.markersHaveInfoWindow) {
      this.gmapsMarkers.forEach((marker) => {
        this.google.maps.event.clearListeners(marker, 'click');
      });
    }
  }

  @Watch('markers')
  recreateMarkers() {
    this.setMapMarkers();
    this.recenter();
  }

  setMapMarkers() {
    this.gmapsMarkers.forEach((marker) => {
      marker.setMap(this.map);
    });
  }

  recenter() {
    this.google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
      if (this.markers.length === 1) {
        this.map.setZoom(14);
      }
    });
    const bounds = new this.google.maps.LatLngBounds();
    this.gmapsMarkers.forEach((marker) => {
      bounds.extend(marker.getPosition());
    });
    this.map.fitBounds(bounds);
  }

  async loadMapScript() {
    if (window.initGMap) return window.initGMap.promise;

    const promise = new Promise((resolve) => {
      const url = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&callback=initGMap`;
      window.initGMap = () => {
        resolve(window.google);
      };
      loadjs(url);
    });

    window.initGMap.promise = promise;
    const google = await promise;
    return google;
  }

  async loadClusterScript() {
    const url = 'https://unpkg.com/@google/markerclustererplus@4.0.1/dist/markerclustererplus.min.js';
    return loadjs(url, {
      numRetries: 3,
      returnPromise: true,
    });
  }

  initializeMap() {
    const mapContainer = this.$refs.googleMap;
    this.map = new this.google.maps.Map(mapContainer, this.mapConfig);
    this.google.maps.event.addListenerOnce(this.map, 'tilesloaded', () => {
      this.map.setZoom(5);
    });
  }

  initializeMarkers() {
    this.gmapsMarkers = this.markers.map((marker) => {
      return new this.google.maps.Marker({
        position: marker.position,
        marker,
        map: this.map,
        ...marker.settings,
        icon: {
          scaledSize: new this.google.maps.Size(
            marker.settings.icon.scaledSize.height,
            marker.settings.icon.scaledSize.width,
          ),
          ...marker.settings.icon,
        },
      });
    });
  }

  /**
   * emit 'click' event for the 'BbGoogleMapInfoWindow' sibling component throught
   * the 'eventBus' instance.
   */
  addMarkersClickListeners() {
    this.gmapsMarkers.forEach((marker) => {
      this.google.maps.event
        .addListener(marker, 'click', () => {
          eventBus.$emit('markerClicked', marker);
        });
    });
  }

  recreateClusters() {
    if (this.gmapsMarkers.length > 3) {
      /* eslint-disable */
      // Disable eslint because this variable isn't used in
      // this file (it's processed by the library)
      const markerCluster = new MarkerClusterer(this.map, this.gmapsMarkers, {
        imagePath: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m',
        minimumClusterSize: 3,
      });
      /* eslint-enable */
    }
  }
}
